import { RadioGroupItem, RadioItemLabel } from '@/shadcn-ui/radio-group';
import { cn } from '@/shadcn-ui/utils';
import PDPPlanHighlightTag from '../../pdp-plan-highlight-tag';
import { colors } from '@/theme/colors';
import CheckSmall from '@/icons/check-small';

export interface PlanRadioButtonCopy {
  id: string;
  name: string;
  pricePerMonthCopy: string;
  details?: string[];
  tag?: PlanTag;
  disabled?: boolean;
  isSwap?: boolean;
}

type PlanTag = 'best-value' | 'recommended';

interface PlanRadioButtonProps {
  plan: PlanRadioButtonCopy;
  selected?: boolean;
  className?: string;
  onClickDelegate?: (e: React.MouseEvent<HTMLLabelElement, MouseEvent>) => void;
}

function planTagCopy(tag?: PlanTag) {
  switch (tag) {
    case 'best-value':
      return 'Best value';
    case 'recommended':
      return 'Recommended';
    // The fallback case should never be reached.
    default:
      return '';
  }
}

export default function PlanRadioButton({
  plan,
  selected = false,
  className,
  onClickDelegate,
}: PlanRadioButtonProps) {
  // We only show the plan tag if it's included and the user is not in the swap flow.
  const hasPlanTag = plan.tag !== undefined && !plan.isSwap;

  return (
    <RadioItemLabel
      key={plan.id}
      htmlFor={plan.name}
      selected={selected}
      disabled={plan.disabled}
      className={cn(
        'items-end bg-[linear-gradient(180deg,_#FFFFFF,_#FBFBFB)] p-0 px-0 pt-[16px]',
        className,
        {
          'cursor-default bg-flexcar-neutral90 bg-none shadow-shadow2 ring-0 hover:shadow-shadow2':
            plan.isSwap,
          'bg-flexcar-neutral100 ring-2 ring-flexcar-neutral1': selected && !plan.isSwap,
          'pb-0': hasPlanTag,
          'pb-[16px]': !hasPlanTag,
          'border-flexcar-neutral80 bg-flexcar-neutral90 bg-none shadow-none hover:shadow-none':
            plan.disabled,
          'shadow-shadow2 hover:shadow-shadow10': !plan.disabled && !plan.isSwap,
        }
      )}
      onClick={onClickDelegate}
    >
      <div className="flex w-full flex-col gap-[8px] px-[16px]">
        {/* Plan title line */}
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center justify-center gap-3">
            <RadioGroupItem
              value={String(plan.id)}
              id={plan.name}
              className="hidden"
              selected={selected}
            />
            <p
              className={cn('body-tight font-semibold leading-[22px]', {
                'text-flexcar-neutral70': plan.disabled,
              })}
            >
              {plan.name}
            </p>
          </div>

          <p
            className={cn('body-tight font-semibold leading-[22px]', {
              'text-flexcar-neutral70': plan.disabled,
            })}
          >
            {plan.pricePerMonthCopy}
          </p>
        </div>

        {/* Plan detail items */}
        {plan.details ? (
          <div className="flex flex-col gap-[4px]">
            {plan.details.map((detail) => (
              <div className="flex flex-row items-center gap-[4px]" key={detail}>
                <CheckSmall
                  className="h-[16px] w-[16px] flex-shrink-0"
                  fillPrimary={plan.disabled ? colors.neutral70 : colors.neutral1}
                />
                <p
                  className={cn('body2 font-sans-tight text-flexcar-neutral1', {
                    'text-flexcar-neutral70': plan.disabled,
                  })}
                >
                  {detail}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {hasPlanTag ? (
        <PDPPlanHighlightTag text={planTagCopy(plan.tag)} disabled={plan.disabled} />
      ) : null}
    </RadioItemLabel>
  );
}
